import { useState } from "react";
import { PaymentHistoryRow } from "./payment-history-row";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CreditCard,
  Wallet,
  FileText,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Loader2,
  Info,
  Calendar,
  Package,
  ExternalLink,
  Download,
  Copy,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  useOrderPaymentStatus,
  useOrderPayments,
  type PaymentStatus,
  type PaymentMethodType,
} from "@/api/payments";
import { useQueryClient } from "@tanstack/react-query";
import { ProcessPaymentDialog } from "../payments/process-payment-dialog";
import { RecordManualPaymentDialog } from "../payments/record-manual-payment-dialog";
import { IssueRefundDialog } from "../payments/issue-refund-dialog";
import { SimplePricingBreakdown } from "@/components/pricing/simple-pricing-breakdown";
import { useSendDraftInvoice } from "@/api/invoices";
import { useRequestShipmentCancellation } from "@/api/shipments";
import { AddOrdersToInvoiceDialog } from "../invoices/add-orders-to-invoice-dialog";
import {
  type Order,
  type Shipment,
  type ShipmentPackage,
  ShipmentStatus,
  type Task,
} from "@/api.types.v2";

// Cancellation reasons for admin
const CANCELLATION_REASONS = [
  { value: "customer_request", label: "Customer requested cancellation" },
  { value: "duplicate_order", label: "Duplicate order" },
  { value: "pricing_error", label: "Pricing error" },
  { value: "address_issue", label: "Address issue - cannot deliver" },
  { value: "out_of_stock", label: "Item out of stock" },
  { value: "shipment_canceled", label: "Shipment canceled by operator" },
  { value: "other", label: "Other (custom)" },
];

interface OrderDetailsWithPaymentProps {
  order: Order;
}

export function OrderDetailsWithPayment({
  order,
}: OrderDetailsWithPaymentProps) {
  const queryClient = useQueryClient();
  const [processPaymentDialogOpen, setProcessPaymentDialogOpen] =
    useState(false);
  const [recordPaymentDialogOpen, setRecordPaymentDialogOpen] = useState(false);
  const [cancelShipmentDialogOpen, setCancelShipmentDialogOpen] =
    useState(false);
  const [shipmentToCancel, setShipmentToCancel] = useState<Shipment | null>(
    null
  );
  const [cancelReasonValue, setCancelReasonValue] = useState("");
  const [cancelCustomReason, setCancelCustomReason] = useState("");
  const [cancelNotes, setCancelNotes] = useState("");
  const [addOrdersDialogOpen, setAddOrdersDialogOpen] = useState(false);
  const [issueRefundDialogOpen, setIssueRefundDialogOpen] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [paymentHistoryExpanded, setPaymentHistoryExpanded] = useState(false);

  const { data: paymentStatus, isLoading: isLoadingStatus } =
    useOrderPaymentStatus(order.id);
  const { data: orderPayments } = useOrderPayments(order.id);

  const sendDraftInvoiceMutation = useSendDraftInvoice();

  // Cancel shipment mutation (unified: immediate for PENDING_PAYMENT, pending for others)
  const cancelShipmentMutation = useRequestShipmentCancellation({
    onSuccess: (data) => {
      if (data.immediate) {
        // PENDING_PAYMENT: Cancelled immediately
        toast.success("Shipment Cancelled", {
          description: data.tasksCancelled
            ? `${data.tasksCancelled} tasks cancelled`
            : data.message,
        });
      } else {
        // Paid shipment: Goes to pending queue for refund review
        toast.success("Cancellation Requested", {
          description:
            "Review in Pending Cancellations to process refund and finalize.",
        });
      }

      void queryClient.invalidateQueries({
        queryKey: ["order-payment-status", order.id],
      });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      void queryClient.invalidateQueries({
        queryKey: ["pending-cancellations"],
      });
      setCancelShipmentDialogOpen(false);
      setShipmentToCancel(null);
      setCancelReasonValue("");
      setCancelCustomReason("");
      setCancelNotes("");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error("Cancellation Failed", {
        description: err?.response?.data?.error ?? "Failed to cancel shipment",
      });
    },
  });

  const handleCancelShipment = () => {
    if (!shipmentToCancel || !cancelReasonValue) {
      toast.error("Reason required", {
        description: "Please select a reason for cancellation",
      });
      return;
    }

    // Build the reason string
    const selectedReason = CANCELLATION_REASONS.find(
      (r) => r.value === cancelReasonValue
    );
    let reason =
      cancelReasonValue === "other"
        ? cancelCustomReason.trim()
        : selectedReason?.label || cancelReasonValue;

    // Append notes if provided
    if (cancelNotes.trim()) {
      reason += ` | Notes: ${cancelNotes.trim()}`;
    }

    if (!reason) {
      toast.error("Reason required", {
        description: "Please provide a custom reason",
      });
      return;
    }

    cancelShipmentMutation.mutate({
      shipmentId: shipmentToCancel.id,
      reason,
    });
  };

  const openCancelShipmentDialog = (shipment: Shipment) => {
    setShipmentToCancel(shipment);
    setCancelShipmentDialogOpen(true);
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getPaymentStatusIcon = (status?: PaymentStatus) => {
    switch (status) {
      case "PAID":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "PENDING":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case "PROCESSING":
        return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      case "FAILED":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "REFUNDED":
        return <RefreshCw className="h-4 w-4 text-purple-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPaymentStatusColor = (status?: PaymentStatus) => {
    switch (status) {
      case "PAID":
        return "bg-green-100 text-green-800";
      case "CREDIT_APPROVED":
        return "bg-cyan-100 text-cyan-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "PROCESSING":
        return "bg-blue-100 text-blue-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      case "REFUNDED":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPaymentMethodIcon = (method?: PaymentMethodType) => {
    switch (method) {
      case "SAVED_CARD":
      case "PAYMENT_LINK":
        return <CreditCard className="h-4 w-4" />;
      case "CREDIT":
        return <Wallet className="h-4 w-4" />;
      case "INVOICE":
        return <FileText className="h-4 w-4" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  const handlePaymentSuccess = () => {
    void queryClient.invalidateQueries({
      queryKey: ["order-payment-status", order.id],
    });
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    setProcessPaymentDialogOpen(false);
    setRecordPaymentDialogOpen(false);
  };

  const canRecordDeferredReceipt =
    paymentStatus?.paymentMethodType === "PAYMENT_LINK" &&
    paymentStatus.remainingCents > 0 &&
    (paymentStatus.paymentStatus === "PENDING" ||
      paymentStatus.paymentStatus === "PARTIAL" ||
      paymentStatus.paymentStatus === "FAILED");

  return (
    <div className="space-y-4">
      {/* Order Information */}
      <div>
        <h4 className="font-semibold mb-2">Order Information</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Order Number:</span> #
            {order.orderNumber}
          </div>
          <div>
            <span className="text-muted-foreground">Created:</span>{" "}
            {new Date(order.createdAt).toLocaleString()}
          </div>
          <div>
            <span className="text-muted-foreground">Updated:</span>{" "}
            {new Date(order.updatedAt).toLocaleString()}
          </div>
          {order.placerCustomer && (
            <div>
              <span className="text-muted-foreground">Customer:</span>{" "}
              {order.placerCustomer.firstName} {order.placerCustomer.lastName}
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Payment Information */}
      <div>
        <h4 className="font-semibold mb-2 flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Payment Information
        </h4>

        {isLoadingStatus ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">
              Loading payment status...
            </span>
          </div>
        ) : (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Payment Status */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <div className="flex items-center gap-2">
                    {getPaymentStatusIcon(paymentStatus?.paymentStatus)}
                    <Badge
                      className={getPaymentStatusColor(
                        paymentStatus?.paymentStatus
                      )}
                    >
                      {paymentStatus?.paymentStatus || "UNKNOWN"}
                    </Badge>
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Method</p>
                  <div className="flex items-center gap-2">
                    {getPaymentMethodIcon(paymentStatus?.paymentMethodType)}
                    <span className="text-sm font-medium">
                      {paymentStatus?.paymentMethodType || "Not selected"}
                    </span>
                  </div>
                </div>

                {/* Status-Based Amounts Display */}
                {/* Total - always shown */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(paymentStatus?.totalCents ?? 0)}
                  </p>
                </div>

                {/* REFUNDED: Originally Paid */}
                {paymentStatus?.paymentStatus === "REFUNDED" && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Originally Paid
                    </p>
                    <p className="text-lg font-semibold text-gray-500">
                      {formatCurrency(
                        paymentStatus?.originallyPaidAmountCents ?? 0
                      )}
                    </p>
                  </div>
                )}

                {/* PAID, PARTIAL, PARTIAL_REFUND: Paid Amount */}
                {(paymentStatus?.paymentStatus === "PAID" ||
                  paymentStatus?.paymentStatus === "PARTIAL" ||
                  paymentStatus?.paymentStatus === "PARTIAL_REFUND") && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Paid</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(paymentStatus?.paidAmountCents ?? 0)}
                    </p>
                  </div>
                )}

                {/* PENDING, PARTIAL: Remaining */}
                {(paymentStatus?.paymentStatus === "PENDING" ||
                  paymentStatus?.paymentStatus === "PARTIAL") &&
                  (paymentStatus?.remainingCents ?? 0) > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Remaining
                      </p>
                      <p className="text-lg font-semibold text-orange-600">
                        {formatCurrency(paymentStatus?.remainingCents ?? 0)}
                      </p>
                    </div>
                  )}

                {/* REFUNDED, PARTIAL_REFUND: Refunded Amount */}
                {(paymentStatus?.paymentStatus === "REFUNDED" ||
                  paymentStatus?.paymentStatus === "PARTIAL_REFUND") &&
                  (paymentStatus?.refundedAmountCents ?? 0) > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Refunded
                      </p>
                      <p className="text-lg font-semibold text-red-600">
                        {formatCurrency(
                          paymentStatus?.refundedAmountCents ?? 0
                        )}
                      </p>
                    </div>
                  )}

                {/* PARTIAL_REFUND: Net Balance */}
                {paymentStatus?.paymentStatus === "PARTIAL_REFUND" && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">
                      Net Balance
                    </p>
                    <p className="text-lg font-semibold text-blue-600">
                      {formatCurrency(
                        (paymentStatus?.paidAmountCents ?? 0) -
                          (paymentStatus?.refundedAmountCents ?? 0)
                      )}
                    </p>
                  </div>
                )}

                {/* Pricing Breakdown */}
                {paymentStatus?.pricingBreakdown && (
                  <div className="col-span-2">
                    <SimplePricingBreakdown
                      /* `carrierCostCents` and `platformFeeCents` are DELETED.
                         They were a THIRD mutable source of truth for money,
                         competing with the quote and the ledger — and an admin
                         reading a stale copy of a payable is how a carrier gets
                         paid the wrong amount. The split lives in FinancialEvent
                         now. Nothing is substituted: a zero would read as "this
                         carrier earned nothing". */
                      breakdown={{
                        baseRateCents:
                          paymentStatus.pricingBreakdown.baseRateCents,
                        weightSurchargeCents:
                          paymentStatus.pricingBreakdown.weightSurchargeCents,
                        pickupFeeCents:
                          paymentStatus.pricingBreakdown.pickupFeeCents,
                        deliveryFeeCents:
                          paymentStatus.pricingBreakdown.deliveryFeeCents,
                        insuranceCents:
                          paymentStatus.pricingBreakdown.insuranceCents,
                        propertyTypeSurchargeCents:
                          paymentStatus.pricingBreakdown
                            .propertyTypeSurchargeCents,
                        subtotalCents:
                          paymentStatus.pricingBreakdown.subtotalCents ?? 0,
                        taxCents: paymentStatus.pricingBreakdown.taxCents ?? 0,
                        totalCents:
                          paymentStatus.pricingBreakdown.totalCents ??
                          paymentStatus.totalCents ??
                          0,
                      }}
                      className="mt-4"
                    />
                  </div>
                )}

                {/* Invoice Information */}
                {paymentStatus?.invoice && (
                  <div className="col-span-2">
                    <div className="flex items-center justify-between mb-3 max-w-md">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">Invoice Details</h4>
                        <Badge
                          variant={
                            paymentStatus.invoice.status === "DRAFT"
                              ? "secondary"
                              : "default"
                          }
                        >
                          {paymentStatus.invoice.status}
                        </Badge>
                      </div>
                      {paymentStatus.invoice.status === "DRAFT" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          disabled={sendDraftInvoiceMutation.isPending}
                          onClick={() => {
                            if (!paymentStatus.invoice) return;
                            sendDraftInvoiceMutation.mutate(
                              { invoiceId: paymentStatus.invoice.id },
                              {
                                onSuccess: () => {
                                  toast.success("Invoice Sent to Stripe", {
                                    description:
                                      "The invoice has been sent to Stripe and the customer will receive it via email.",
                                  });
                                },
                                onError: (error: unknown) => {
                                  const err = error as {
                                    response?: { data?: { error?: string } };
                                  };
                                  toast.error("Failed to Send Invoice", {
                                    description:
                                      err.response?.data?.error ??
                                      "Failed to send invoice to Stripe",
                                  });
                                },
                              }
                            );
                          }}
                        >
                          {sendDraftInvoiceMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            "Finalize & Send Invoice"
                          )}
                        </Button>
                      )}
                    </div>
                    <div className="space-y-1 min-w-[300px] max-w-md border rounded-lg p-3 bg-gray-50">
                      {paymentStatus.invoice.number && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Invoice Number</span>
                          <span>#{paymentStatus.invoice.number}</span>
                        </div>
                      )}

                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Created</span>
                        <span>
                          {new Date(
                            paymentStatus.invoice.createdAt
                          ).toLocaleDateString()}
                        </span>
                      </div>

                      {paymentStatus.invoice.dueDate && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Due Date</span>
                          <span>
                            {new Date(
                              paymentStatus.invoice.dueDate
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}

                      {paymentStatus.invoice.orderDetails && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Orders Included</span>
                          <span>
                            {paymentStatus.invoice.orderDetails.orderCount}{" "}
                            order
                            {paymentStatus.invoice.orderDetails.orderCount !== 1
                              ? "s"
                              : ""}
                          </span>
                        </div>
                      )}

                      {paymentStatus.invoice.orderDetails?.orderNumbers &&
                        paymentStatus.invoice.orderDetails.orderNumbers.length >
                          0 && (
                          <>
                            <div className="border-t pt-1" />
                            <div className="text-xs">
                              <div className="text-gray-600 mb-1">
                                Order Numbers
                              </div>
                              <div>
                                #
                                {paymentStatus.invoice.orderDetails.orderNumbers.join(
                                  ", #"
                                )}
                              </div>
                            </div>
                          </>
                        )}

                      {paymentStatus.invoice.orderDetails?.dateRange && (
                        <>
                          <div className="border-t pt-1" />
                          <div className="text-xs">
                            <div className="text-gray-600 mb-1">
                              Order Period
                            </div>
                            <div>
                              {new Date(
                                paymentStatus.invoice.orderDetails.dateRange
                                  .start
                              ).toLocaleDateString()}{" "}
                              -{" "}
                              {new Date(
                                paymentStatus.invoice.orderDetails.dateRange.end
                              ).toLocaleDateString()}
                            </div>
                          </div>
                        </>
                      )}

                      {paymentStatus.invoice.orderDetails && (
                        <>
                          <div className="border-t pt-1" />
                          <div className="flex justify-between text-xs font-semibold">
                            <span>Invoice Total</span>
                            <span>
                              {formatCurrency(
                                paymentStatus.invoice.orderDetails.totalCents
                              )}
                            </span>
                          </div>
                        </>
                      )}

                      {(paymentStatus.invoice.hostedUrl ||
                        paymentStatus.invoice.pdfUrl) && (
                        <>
                          <div className="border-t pt-2 mt-1" />
                          <div className="flex gap-2">
                            {paymentStatus.invoice?.hostedUrl && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  window.open(
                                    paymentStatus.invoice?.hostedUrl ??
                                      undefined,
                                    "_blank"
                                  )
                                }
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View Invoice
                              </Button>
                            )}
                            {paymentStatus.invoice?.pdfUrl && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  window.open(
                                    paymentStatus.invoice?.pdfUrl ?? undefined,
                                    "_blank"
                                  )
                                }
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Add Orders Button for DRAFT invoices */}
                    {paymentStatus.invoice.status === "DRAFT" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => setAddOrdersDialogOpen(true)}
                      >
                        Add Orders to Invoice
                      </Button>
                    )}
                  </div>
                )}

                {/* NET Terms Information */}
                {paymentStatus?.netTermsInfo && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      NET Terms Information
                    </p>
                    <Alert>
                      <AlertDescription className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">
                            {paymentStatus.netTermsInfo.paymentTerms.name}
                          </span>
                          <Badge variant="secondary">
                            {
                              paymentStatus.netTermsInfo.paymentTerms
                                .paymentDueDays
                            }{" "}
                            days
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Pending Orders
                              </p>
                              <p className="font-medium">
                                {paymentStatus.netTermsInfo.pendingOrderCount}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Pending Amount
                              </p>
                              <p className="font-medium">
                                {formatCurrency(
                                  paymentStatus.netTermsInfo
                                    .pendingAmountCents || 0
                                )}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Days Until Invoice
                              </p>
                              <p className="font-medium">
                                {paymentStatus.netTermsInfo.daysUntilInvoice}{" "}
                                days
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Next Invoice Date
                              </p>
                              <p className="font-medium">
                                {new Date(
                                  paymentStatus.netTermsInfo
                                    .estimatedInvoiceDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="pt-2 border-t">
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                              {
                                paymentStatus.netTermsInfo.paymentTerms
                                  .dayInterval
                              }
                              -day billing cycle
                            </p>
                            {paymentStatus.netTermsInfo.pendingAmountCents >
                              0 && (
                              <span className="text-sm font-medium text-green-600">
                                {formatCurrency(
                                  paymentStatus.netTermsInfo.pendingAmountCents
                                )}
                              </span>
                            )}
                          </div>
                        </div>

                        {paymentStatus.paymentDueDate && (
                          <div className="text-xs">
                            <span className="text-muted-foreground">
                              Payment Due:{" "}
                            </span>
                            <span className="font-medium">
                              {new Date(
                                paymentStatus.paymentDueDate
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </div>

              {/* Payment History Section */}
              {orderPayments && orderPayments.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() =>
                      setPaymentHistoryExpanded(!paymentHistoryExpanded)
                    }
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground w-full"
                  >
                    {paymentHistoryExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    Payment History ({orderPayments.length} transaction
                    {orderPayments.length !== 1 ? "s" : ""})
                  </button>
                  {paymentHistoryExpanded && (
                    <div className="mt-3 space-y-2">
                      {[...orderPayments]
                        .sort(
                          (a, b) =>
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime()
                        )
                        .map((payment) => (
                          <PaymentHistoryRow
                            key={payment.id}
                            payment={payment}
                          />
                        ))}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Actions */}
              <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                {paymentStatus?.paymentStatus !== "PAID" &&
                  paymentStatus?.paymentStatus !== "REFUNDED" && (
                    <>
                      {/* Card/Credit Payment - Process immediately */}
                      {(paymentStatus?.paymentMethodType === "SAVED_CARD" ||
                        paymentStatus?.paymentMethodType === "CREDIT") && (
                        <Button
                          size="sm"
                          onClick={() => setProcessPaymentDialogOpen(true)}
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Process Payment
                        </Button>
                      )}

                      {/* Manual payment methods - Mark as received */}
                      {(paymentStatus?.paymentMethodType === "CASH" ||
                        paymentStatus?.paymentMethodType === "E_TRANSFER" ||
                        paymentStatus?.paymentMethodType === "WECHAT") && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => setRecordPaymentDialogOpen(true)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark Payment Received
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setShowInstructions(!showInstructions)
                            }
                          >
                            <Info className="h-4 w-4 mr-2" />
                            {showInstructions ? "Hide" : "Show"} Instructions
                          </Button>
                        </>
                      )}

                      {/* Decide later may be settled with a manual receipt.
                          The server still rejects conflicting card attempts. */}
                      {canRecordDeferredReceipt && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRecordPaymentDialogOpen(true)}
                        >
                          <DollarSign className="h-4 w-4 mr-2" />
                          Record Manual Payment
                        </Button>
                      )}

                      {/* No payment method selected */}
                      {!paymentStatus?.paymentMethodType && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => setProcessPaymentDialogOpen(true)}
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Process Payment
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRecordPaymentDialogOpen(true)}
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Record Manual Payment
                          </Button>
                        </>
                      )}
                    </>
                  )}

                {/* Create a component-based refund request for a paid order. */}
                {(paymentStatus?.paymentStatus === "PAID" ||
                  paymentStatus?.paymentStatus === "PARTIAL" ||
                  paymentStatus?.paymentStatus === "PARTIAL_REFUND") &&
                  (paymentStatus?.paidAmountCents ?? 0) > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setIssueRefundDialogOpen(true)}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Create Refund Request
                    </Button>
                  )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Separator />

      {/* Shipments */}
      {order.shipments && order.shipments.length > 0 && (
        <div>
          <h4 className="font-semibold mb-3">
            Shipments ({order.shipments.length})
          </h4>
          <div className="space-y-4">
            {order.shipments.map((shipment: Shipment, index: number) => {
              const statusColors: Record<string, string> = {
                PENDING_PAYMENT: "bg-amber-100 text-amber-800",
                LABEL_READY: "bg-blue-100 text-blue-800",
                PICKED_UP: "bg-purple-100 text-purple-800",
                IN_TRANSIT: "bg-indigo-100 text-indigo-800",
                AT_FACILITY: "bg-cyan-100 text-cyan-800",
                OUT_FOR_DELIVERY: "bg-yellow-100 text-yellow-800",
                DELIVERED: "bg-green-100 text-green-800",
                AVAILABLE_FOR_PICKUP: "bg-teal-100 text-teal-800",
                DELIVERY_ATTEMPTED: "bg-orange-100 text-orange-800",
                EXCEPTION: "bg-red-200 text-red-900",
                ON_HOLD: "bg-amber-100 text-amber-800",
                RETURNED: "bg-stone-100 text-stone-800",
                CANCELLED: "bg-red-100 text-red-800",
              };

              const carrierName =
                shipment.carrier?.name ||
                shipment.carrierCode ||
                shipment.carrierId;
              const serviceName = shipment.serviceCode;

              return (
                <Card key={shipment.id} className="overflow-hidden">
                  {/* Header */}
                  <div className="bg-muted/50 px-4 py-3 flex items-center justify-between border-b">
                    <div className="flex items-center gap-3">
                      <span className="font-medium">
                        {shipment.shipmentNumber
                          ? `SHP-${shipment.shipmentNumber}`
                          : `Shipment ${index + 1}`}
                      </span>
                      <Badge
                        className={
                          statusColors[shipment.status] || "bg-gray-100"
                        }
                      >
                        {shipment.status?.replace(/_/g, " ")}
                      </Badge>
                      {shipment.providerRef && (
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                          {shipment.providerRef}
                        </code>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {shipment.sellAmountCents != null && (
                        <span className="text-sm font-medium">
                          ${(shipment.sellAmountCents / 100).toFixed(2)}
                        </span>
                      )}
                      {(shipment.status === ShipmentStatus.PENDING_PAYMENT ||
                        shipment.status === ShipmentStatus.LABEL_READY) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => openCancelShipmentDialog(shipment)}
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Carrier & Tracking Info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div>
                          <span className="text-muted-foreground text-xs">
                            Carrier
                          </span>
                          <p className="font-medium">{carrierName}</p>
                        </div>
                        {serviceName && (
                          <div>
                            <span className="text-muted-foreground text-xs">
                              Service
                            </span>
                            <p>{serviceName}</p>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        {shipment.trackingNumber && (
                          <div>
                            <span className="text-muted-foreground text-xs">
                              Tracking
                            </span>
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/tracking/${shipment.trackingNumber}`}
                                className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {shipment.trackingNumber}
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  void navigator.clipboard.writeText(
                                    shipment.trackingNumber ?? ""
                                  );
                                  toast.success("Tracking number copied");
                                }}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                        {(shipment.shipDate || shipment.estimatedDelivery) && (
                          <div>
                            <span className="text-muted-foreground text-xs">
                              Dates
                            </span>
                            <div className="text-xs space-y-0.5">
                              {shipment.shipDate && (
                                <p>
                                  Ship:{" "}
                                  {new Date(
                                    shipment.shipDate
                                  ).toLocaleDateString()}
                                </p>
                              )}
                              {shipment.estimatedDelivery && (
                                <p>
                                  Est. Delivery:{" "}
                                  {new Date(
                                    shipment.estimatedDelivery
                                  ).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Packages */}
                    {shipment.packages && shipment.packages.length > 0 && (
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Packages ({shipment.packages.length})
                        </span>
                        <div className="mt-1 space-y-1">
                          {shipment.packages.map(
                            (pkg: ShipmentPackage, pkgIndex: number) => (
                              <div
                                key={pkg.id}
                                className="flex items-center gap-3 text-xs bg-muted/30 rounded px-2 py-1.5"
                              >
                                <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="font-medium">
                                  #{pkgIndex + 1}
                                </span>
                                <span>{pkg.weightGrams}g</span>
                                <span className="text-muted-foreground">
                                  {pkg.lengthCm} × {pkg.widthCm} ×{" "}
                                  {pkg.heightCm} cm
                                </span>
                                {pkg.individualTrackingNumber && (
                                  <code className="ml-auto text-[10px] bg-muted px-1 rounded">
                                    {pkg.individualTrackingNumber}
                                  </code>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {/* Tasks */}
                    {shipment.tasks && shipment.tasks.length > 0 && (
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Tasks ({shipment.tasks.length})
                        </span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {shipment.tasks.map((task: Task) => {
                            const taskStatusColors: Record<string, string> = {
                              PENDING: "bg-yellow-100 text-yellow-800",
                              IN_PROGRESS: "bg-blue-100 text-blue-800",
                              COMPLETED: "bg-green-100 text-green-800",
                              CANCELLED: "bg-red-100 text-red-800",
                              FAILED: "bg-red-200 text-red-900",
                            };
                            return (
                              <div
                                key={task.id}
                                className="flex items-center gap-1.5 text-xs"
                              >
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-1.5 py-0 ${taskStatusColors[task.status] || ""}`}
                                >
                                  {task.status}
                                </Badge>
                                <span className="text-muted-foreground">
                                  {task.type}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Process Payment Dialog */}
      <ProcessPaymentDialog
        open={processPaymentDialogOpen}
        onOpenChange={setProcessPaymentDialogOpen}
        order={{
          id: order.id,
          orderNumber: order.orderNumber,
          balanceCents: paymentStatus?.remainingCents ?? 0,
          paymentMethodType: paymentStatus?.paymentMethodType,
        }}
        onSuccess={handlePaymentSuccess}
      />

      {/* Record Manual Payment Dialog */}
      <RecordManualPaymentDialog
        open={recordPaymentDialogOpen}
        onOpenChange={setRecordPaymentDialogOpen}
        order={{
          id: order.id,
          orderNumber: order.orderNumber,
          balanceCents: paymentStatus?.remainingCents ?? 0,
        }}
        onSuccess={handlePaymentSuccess}
      />

      {/* Cancel Shipment Dialog */}
      <Dialog
        open={cancelShipmentDialogOpen}
        onOpenChange={(open) => {
          setCancelShipmentDialogOpen(open);
          if (!open) {
            setShipmentToCancel(null);
            setCancelReasonValue("");
            setCancelCustomReason("");
            setCancelNotes("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Shipment</DialogTitle>
            <DialogDescription>
              Cancel SHP-
              {shipmentToCancel?.shipmentNumber ?? ""}
              {shipmentToCancel?.sellAmountCents != null && (
                <> (${(shipmentToCancel.sellAmountCents / 100).toFixed(2)})</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                {shipmentToCancel?.status === ShipmentStatus.PENDING_PAYMENT
                  ? "This will cancel all tasks for this shipment."
                  : "This will cancel all tasks. You can process refunds from Pending Cancellations."}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Cancellation Reason</Label>
              <Select
                value={cancelReasonValue}
                onValueChange={setCancelReasonValue}
                disabled={cancelShipmentMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {CANCELLATION_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {cancelReasonValue === "other" && (
              <div className="space-y-2">
                <Label htmlFor="custom-reason">Custom Reason</Label>
                <Textarea
                  id="custom-reason"
                  placeholder="Enter the reason for cancellation..."
                  value={cancelCustomReason}
                  onChange={(e) => setCancelCustomReason(e.target.value)}
                  rows={2}
                  disabled={cancelShipmentMutation.isPending}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="cancel-notes">Notes (optional)</Label>
              <Textarea
                id="cancel-notes"
                placeholder="Additional notes..."
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
                rows={2}
                disabled={cancelShipmentMutation.isPending}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCancelShipmentDialogOpen(false);
                setShipmentToCancel(null);
                setCancelReasonValue("");
                setCancelCustomReason("");
                setCancelNotes("");
              }}
            >
              Keep Shipment
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelShipment}
              disabled={
                cancelShipmentMutation.isPending ||
                !cancelReasonValue ||
                (cancelReasonValue === "other" && !cancelCustomReason.trim())
              }
            >
              {cancelShipmentMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Cancel Shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Orders to Invoice Dialog */}
      {paymentStatus?.invoice && (
        <AddOrdersToInvoiceDialog
          open={addOrdersDialogOpen}
          onOpenChange={setAddOrdersDialogOpen}
          invoiceId={paymentStatus.invoice.id}
          onSuccess={() => {
            void queryClient.invalidateQueries({
              queryKey: ["order-payment-status", order.id],
            });
            void queryClient.invalidateQueries({ queryKey: ["invoices"] });
          }}
        />
      )}

      {/* Canonical refund-request initiation dialog */}
      <IssueRefundDialog
        open={issueRefundDialogOpen}
        onOpenChange={setIssueRefundDialogOpen}
        orderId={order.id}
        orderNumber={order.orderNumber}
        paidAmountCents={paymentStatus?.paidAmountCents ?? 0}
        onSuccess={() => {
          void queryClient.invalidateQueries({
            queryKey: ["order-payment-status", order.id],
          });
          void queryClient.invalidateQueries({
            queryKey: ["order-payments", order.id],
          });
          void queryClient.invalidateQueries({ queryKey: ["orders"] });
        }}
      />
    </div>
  );
}
